import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faChartLine, faBoxes, faTachometerAlt, faChartPie } from '@fortawesome/free-solid-svg-icons';
import { Doughnut } from 'react-chartjs-2';
import { ApiManager } from '../../../core/api-manager';
import { toast } from 'react-toastify';

const OverviewTab = ({ timeRange, customDateRange, chartOptions }) => {
    const [loading, setLoading] = useState(true);
    const [overviewData, setOverviewData] = useState(null);
    const [availableEntities, setAvailableEntities] = useState([]);
    const [hiddenEntities, setHiddenEntities] = useState(['user_group', 'auth_token', 'auth_pin', 'user_entity_saved_filters', 'user_entity_preferences', 'log']);

    useEffect(() => {
        loadOverviewData();
    }, [timeRange, customDateRange]);

    const loadOverviewData = async () => {
        setLoading(true);
        try {
            // Preparazione parametri per l'API
            const apiParams = { timeRange };
            if (timeRange === 'custom' && customDateRange.start && customDateRange.end) {
                apiParams.startDate = customDateRange.start;
                apiParams.endDate = customDateRange.end;
            }

            const response = await ApiManager.sendAuthenticatedRequest('/analytics/overview', apiParams);

            const processedData = response?.data || response;
            setOverviewData(processedData);
            
            if (processedData?.availableEntities) {
                setAvailableEntities(processedData.availableEntities);
            }
        } catch (error) {
            console.error('Error loading overview data:', error);
            toast.error('Errore nel caricamento dati overview');
        } finally {
            setLoading(false);
        }
    };

    // Ottieni tutte le entità (visibili e nascoste)
    const getAllEntities = () => {
        return overviewData?.entities || {};
    };

    // Filtra entità per il grafico (solo quelle non nascoste)
    const getChartEntities = () => {
        if (!overviewData?.entities) return {};
        
        const filtered = {};
        Object.entries(overviewData.entities).forEach(([entity, data]) => {
            if (!hiddenEntities.includes(entity)) {
                filtered[entity] = data;
            }
        });
        return filtered;
    };

    // Toggle visibilità entità
    const toggleEntityVisibility = (entity) => {
        setHiddenEntities(prev => {
            if (prev.includes(entity)) {
                return prev.filter(e => e !== entity);
            } else {
                return [...prev, entity];
            }
        });
    };

    // Componente KPI Card
    const KPICard = ({ title, value, change, icon, color = 'primary' }) => (
        <Card className="kpi-card h-100">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <p className="text-muted small mb-1">{title}</p>
                        <h3 className="mb-0">{value}</h3>
                        {change && (
                            <Badge 
                                bg={change > 0 ? 'success' : 'danger'} 
                                className="mt-2"
                            >
                                {change > 0 ? '+' : ''}{change}%
                            </Badge>
                        )}
                    </div>
                    <div className={`kpi-icon text-${color}`}>
                        <FontAwesomeIcon icon={icon} size="2x" />
                    </div>
                </div>
            </Card.Body>
        </Card>
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <>
            {/* KPI Cards */}
            <Row className="g-3 mb-4">
                <Col lg={3} md={6}>
                    <KPICard 
                        title="Utenti Attivi"
                        value={overviewData?.activeUsers || 0}
                        change={overviewData?.usersChange}
                        icon={faUsers}
                        color="primary"
                    />
                </Col>
                <Col lg={3} md={6}>
                    <KPICard 
                        title="Operazioni"
                        value={overviewData?.todayOperations || 0}
                        change={overviewData?.operationsChange}
                        icon={faChartLine}
                        color="success"
                    />
                </Col>
                <Col lg={3} md={6}>
                    <KPICard 
                        title="Entità Totali"
                        value={availableEntities?.length || overviewData?.availableEntities?.length || 0}
                        icon={faBoxes}
                        color="info"
                    />
                </Col>
                <Col lg={3} md={6}>
                    <KPICard 
                        title="Sistema"
                        value="Attivo"
                        icon={faTachometerAlt}
                        color="warning"
                    />
                </Col>
            </Row>

            {/* Distribuzione Entità */}
            <Row className="g-3">
                <Col lg={12}>
                    <Card>
                        <Card.Header>
                            <Card.Title>Distribuzione Entità</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            {(() => {
                                const allEntities = getAllEntities();
                                const chartEntities = getChartEntities();
                                const allEntityKeys = Object.keys(allEntities);
                                const chartEntityKeys = Object.keys(chartEntities);
                                
                                if (allEntityKeys.length === 0) {
                                    return (
                                        <div className="d-flex align-items-center justify-content-center py-5 text-muted">
                                            <div className="text-center">
                                                <FontAwesomeIcon icon={faChartPie} size="3x" className="mb-3" />
                                                <p>Nessun dato disponibile per la distribuzione entità</p>
                                            </div>
                                        </div>
                                    );
                                }

                                const colors = [
                                    '#5887da', '#28a745', '#ffc107', '#dc3545', '#17a2b8',
                                    '#6c757d', '#fd7e14', '#e83e8c', '#20c997', '#6f42c1',
                                    '#ff6384', '#36a2eb', '#cc65fe', '#ffce56', '#4bc0c0',
                                    '#9966ff', '#ff9f40', '#ff6633', '#45b7d1', '#96ceb4'
                                ];

                                return (
                                    <>
                                        {/* Lista entità sopra il grafico */}
                                        <div className="mb-4">
                                            <h6 className="text-muted mb-3">Entità del Sistema (clicca per mostrare/nascondere)</h6>
                                            <div className="row g-2">
                                                {allEntityKeys.map((entity, index) => {
                                                    const isHidden = hiddenEntities.includes(entity);
                                                    return (
                                                        <div key={entity} className="col-xl-2-4 col-lg-3 col-md-4 col-sm-6">
                                                            <div 
                                                                className={`d-flex align-items-center p-2 border rounded ${isHidden ? 'bg-light opacity-50' : ''}`}
                                                                style={{ 
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s'
                                                                }}
                                                                onClick={() => toggleEntityVisibility(entity)}
                                                                title={isHidden ? 'Clicca per mostrare nel grafico' : 'Clicca per nascondere dal grafico'}
                                                            >
                                                                <div 
                                                                    className="me-2"
                                                                    style={{
                                                                        width: '12px',
                                                                        height: '12px',
                                                                        backgroundColor: isHidden ? '#ccc' : colors[index % colors.length],
                                                                        borderRadius: '50%',
                                                                        transition: 'background-color 0.2s'
                                                                    }}
                                                                />
                                                                <div className="flex-grow-1">
                                                                    <div className={`small fw-bold ${isHidden ? 'text-muted' : ''}`}>
                                                                        {entity.charAt(0).toUpperCase() + entity.slice(1).replace(/_/g, ' ')}
                                                                    </div>
                                                                    <div className="text-muted small">
                                                                        {allEntities[entity]?.total || 0} record
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Grafico a torta */}
                                        {chartEntityKeys.length > 0 ? (
                                            <div style={{ height: '400px', position: 'relative' }}>
                                                <Doughnut 
                                                    data={{
                                                        labels: chartEntityKeys.map(entity => 
                                                            entity.charAt(0).toUpperCase() + entity.slice(1).replace(/_/g, ' ')
                                                        ),
                                                        datasets: [{
                                                            data: chartEntityKeys.map(entity => chartEntities[entity]?.total || 0),
                                                            backgroundColor: chartEntityKeys.map(entity => {
                                                                const originalIndex = allEntityKeys.indexOf(entity);
                                                                return colors[originalIndex % colors.length];
                                                            })
                                                        }]
                                                    }}
                                                    options={{
                                                        ...chartOptions,
                                                        maintainAspectRatio: false,
                                                        plugins: {
                                                            ...chartOptions.plugins,
                                                            legend: {
                                                                display: false
                                                            },
                                                            tooltip: {
                                                                callbacks: {
                                                                    label: function(context) {
                                                                        const entity = chartEntityKeys[context.dataIndex];
                                                                        const data = chartEntities[entity];
                                                                        return `${entity}: ${data?.total || 0} record`;
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="text-center py-5 text-muted">
                                                <p>Seleziona almeno un'entità per visualizzare il grafico</p>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default OverviewTab;